import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useLazyGetWarehouseProductLeftoverControlQuery,
  useSetProductSalePriceAndLeftoverControlMutation,
  type ModelProductLimits,
  type ModelProductSalePriceAndLeftovers,
} from '@app/api/Warehouses';
import type {
  PercentDirection,
  PriceChangeType,
} from '@app/components/Modals/EditProductGroupSalePriceModal/types';
import type { EditProductGroupSalePriceModalProps } from '@app/components/Modals/EditProductGroupSalePriceModal/ui/EditProductGroupSalePriceModal.main';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

interface UseEditProductGroupSalePriceModalReturn {
  isSetSalePrice: boolean;
  setIsSetSalePrice(value: boolean): void;
  priceChangeType: PriceChangeType;
  setPriceChangeType(value: PriceChangeType): void;
  percentDirection: PercentDirection;
  setPercentDirection(value: PercentDirection): void;
  priceValue: number | null;
  setPriceValue(value: number | null): void;

  isSetLeftoverControl: boolean;
  setIsSetLeftoverControl(value: boolean): void;
  leftoverMinimal: number | null;
  setLeftoverMinimal(value: number | null): void;
  isNotifyAtMinimal: boolean;
  setIsNotifyAtMinimal(value: boolean): void;
  leftoverCritical: number | null;
  setLeftoverCritical(value: number | null): void;
  isNotifyAtCritical: boolean;
  setIsNotifyAtCritical(value: boolean): void;

  isSaving: boolean;
  isLoadingLeftoverControl: boolean;
  isLeftoverControlLoadFailed: boolean;
  canSubmit: boolean;
  saveHandler(): Promise<void>;
}

export const useEditProductGroupSalePriceModal = ({
  productId,
  warehouseId,
  open,
  onClose,
  afterSave,
  forceLeftoverControl,
}: EditProductGroupSalePriceModalProps): UseEditProductGroupSalePriceModalReturn => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [isSetSalePrice, setIsSetSalePrice] = useState<boolean>(false);
  const [priceChangeType, setPriceChangeType] =
    useState<PriceChangeType>('fixed');
  const [percentDirection, setPercentDirection] =
    useState<PercentDirection>('increase');
  const [priceValue, setPriceValue] = useState<number | null>(null);

  const [isSetLeftoverControl, setIsSetLeftoverControl] =
    useState<boolean>(false);
  const [leftoverMinimal, setLeftoverMinimal] = useState<number | null>(null);
  const [isNotifyAtMinimal, setIsNotifyAtMinimal] = useState<boolean>(false);
  const [leftoverCritical, setLeftoverCritical] = useState<number | null>(null);
  const [isNotifyAtCritical, setIsNotifyAtCritical] = useState<boolean>(false);
  const [isLeftoverControlLoaded, setIsLeftoverControlLoaded] =
    useState<boolean>(false);
  const [isLeftoverControlLoadFailed, setIsLeftoverControlLoadFailed] =
    useState<boolean>(false);
  const loadRequestIdRef = useRef<number>(0);

  const [setProductSalePriceAndLeftoverControl, { isLoading: isSaving }] =
    useSetProductSalePriceAndLeftoverControlMutation();
  const [
    getWarehouseProductLeftoverControl,
    { isFetching: isLoadingLeftoverControl },
  ] = useLazyGetWarehouseProductLeftoverControlQuery();

  const resetState = useCallback(() => {
    setIsSetSalePrice(false);
    setPriceChangeType('fixed');
    setPercentDirection('increase');
    setPriceValue(null);

    setIsSetLeftoverControl(false);
    setLeftoverMinimal(null);
    setIsNotifyAtMinimal(false);
    setLeftoverCritical(null);
    setIsNotifyAtCritical(false);
    setIsLeftoverControlLoaded(false);
    setIsLeftoverControlLoadFailed(false);
  }, []);

  const setLoadedLeftoverControl = useCallback(
    (limits: ModelProductLimits, forceEnabled = false) => {
      const hasMinimalLimit = limits.CountMinimal != null;
      const hasCriticalLimit = limits.CountCritical != null;

      setIsSetLeftoverControl(
        forceEnabled || hasMinimalLimit || hasCriticalLimit
      );
      setLeftoverMinimal(limits.CountMinimal ?? null);
      setIsNotifyAtMinimal(hasMinimalLimit && limits.IsNotifyMinimal);
      setLeftoverCritical(limits.CountCritical ?? null);
      setIsNotifyAtCritical(hasCriticalLimit && limits.IsNotifyCritical);
    },
    []
  );

  useEffect(() => {
    if (!open) {
      loadRequestIdRef.current += 1;
      resetState();
      return;
    }

    if (warehouseId == null || productId == null || ActiveBranchId == null) {
      loadRequestIdRef.current += 1;
      resetState();
      return;
    }

    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    resetState();

    const loadLeftoverControl = async () => {
      try {
        const limits = await getWarehouseProductLeftoverControl({
          ActiveBranchId,
          warehouseId,
          productId,
        }).unwrap();

        if (loadRequestIdRef.current !== requestId) return;

        setLoadedLeftoverControl(limits, forceLeftoverControl);
        setIsLeftoverControlLoaded(true);
      } catch (error) {
        if (loadRequestIdRef.current !== requestId) return;

        setIsLeftoverControlLoadFailed(true);
        showNotification(error);
      }
    };

    void loadLeftoverControl();
  }, [
    ActiveBranchId,
    forceLeftoverControl,
    getWarehouseProductLeftoverControl,
    open,
    productId,
    resetState,
    setLoadedLeftoverControl,
    showNotification,
    warehouseId,
  ]);

  const canSubmit =
    !isSaving &&
    !isLoadingLeftoverControl &&
    isLeftoverControlLoaded &&
    !isLeftoverControlLoadFailed;

  const saveHandler = useCallback(async () => {
    if (!canSubmit) return;

    const resolvedSalePrice = isSetSalePrice
      ? priceChangeType === 'percent' && percentDirection === 'decrease'
        ? -(priceValue ?? 0)
        : priceValue ?? 0
      : null;

    const payload: ModelProductSalePriceAndLeftovers = {
      ProductId: productId,
      WarehouseId: warehouseId,
      IsSetSalePrice: isSetSalePrice,
      PriceChangeType: isSetSalePrice ? priceChangeType : null,
      SalePrice: resolvedSalePrice,
      IsSetLeftoverControl: isSetLeftoverControl,
      LeftoverMinimal: isSetLeftoverControl ? leftoverMinimal ?? 0 : null,
      IsNotifyAtMinimal: isSetLeftoverControl && isNotifyAtMinimal,
      LeftoverCritical: isSetLeftoverControl ? leftoverCritical ?? 0 : null,
      IsNotifyAtCritical: isSetLeftoverControl && isNotifyAtCritical,
    };

    try {
      await setProductSalePriceAndLeftoverControl({
        ActiveBranchId,
        data: payload,
      }).unwrap();
      showNotification('Настройки товара обновлены', 'success');
      afterSave?.();
      onClose();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    productId,
    warehouseId,
    canSubmit,
    isSetSalePrice,
    priceChangeType,
    percentDirection,
    priceValue,
    isSetLeftoverControl,
    leftoverMinimal,
    isNotifyAtMinimal,
    leftoverCritical,
    isNotifyAtCritical,
    setProductSalePriceAndLeftoverControl,
    showNotification,
    afterSave,
    onClose,
  ]);

  return {
    isSetSalePrice,
    setIsSetSalePrice,
    priceChangeType,
    setPriceChangeType,
    percentDirection,
    setPercentDirection,
    priceValue,
    setPriceValue,

    isSetLeftoverControl,
    setIsSetLeftoverControl,
    leftoverMinimal,
    setLeftoverMinimal,
    isNotifyAtMinimal,
    setIsNotifyAtMinimal,
    leftoverCritical,
    setLeftoverCritical,
    isNotifyAtCritical,
    setIsNotifyAtCritical,

    isSaving,
    isLoadingLeftoverControl,
    isLeftoverControlLoadFailed,
    canSubmit,
    saveHandler,
  };
};
